import {
  toXmaxSdkError,
  XmaxSdkError,
} from '../../shared/sdk-error'

export type CameraPreflightOptions = {
  facingMode?: 'user' | 'environment'
  width: number
  height: number
  frameRate: number
}

function isOverconstrainedError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false
  }
  const name = (error as { name?: unknown }).name
  return name === 'OverconstrainedError' || name === 'ConstraintNotSatisfiedError'
}

function stopStreamTracks(stream: MediaStream | null): void {
  if (!stream) {
    return
  }
  stream.getTracks().forEach((track) => {
    try {
      track.stop()
    } catch {
      // A failed cleanup must not hide the original camera access result.
    }
  })
}

function createPreferredConstraints(options: CameraPreflightOptions): MediaStreamConstraints {
  const video: MediaTrackConstraints = {
    width: { ideal: options.width },
    height: { ideal: options.height },
    frameRate: { ideal: options.frameRate },
  }
  if (options.facingMode) {
    video.facingMode = { ideal: options.facingMode }
  }
  return {
    video,
    // Camera availability is the gate. Microphone capture remains owned by the RTC flow.
    audio: false,
  }
}

/**
 * Open and immediately release a browser camera before a billable Session is created.
 * This performs no frame polling, timers, or persistent rendering work.
 */
export async function preflightCameraAccess(options: CameraPreflightOptions): Promise<void> {
  if (
    typeof navigator === 'undefined'
    || !navigator.mediaDevices
    || typeof navigator.mediaDevices.getUserMedia !== 'function'
  ) {
    throw new XmaxSdkError('CAMERA_ERROR', 'Camera is not supported in this browser')
  }

  let stream: MediaStream | null = null
  try {
    try {
      stream = await navigator.mediaDevices.getUserMedia(createPreferredConstraints(options))
    } catch (error) {
      if (!isOverconstrainedError(error)) {
        throw error
      }
      // Older and embedded browsers can reject otherwise valid ideal constraints.
      stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false })
    }

    const videoTrack = stream.getVideoTracks()[0]
    if (!videoTrack || videoTrack.readyState !== 'live') {
      throw new XmaxSdkError('CAMERA_ERROR', 'No active video track')
    }
  } catch (error) {
    throw toXmaxSdkError(error, {
      code: 'CAMERA_ERROR',
      message: 'Failed to access camera',
    })
  } finally {
    stopStreamTracks(stream)
  }
}
